'use client'

import type { Fetchable } from '@morpho-org/blue-sdk'

import { forwardRef } from 'react'

import { Avatar, type AvatarSize } from './Avatar'
import { AvatarPair } from './AvatarPair'

export interface MarketAvatarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** URI for the loan asset logo — always rendered, leftmost */
  loanAssetURI: Fetchable<string>
  /**
   * URI for the collateral asset logo.
   *
   * Omit it (or pass `undefined`) only when the market genuinely has no collateral,
   * which renders a single loan avatar. A collateralized market whose token has no
   * logo must pass `null` — the pair still renders, with a fallback on the collateral
   * side. Callers get this for free by passing `collateralAsset?.logoURI`.
   */
  collateralAssetURI?: Fetchable<string>
  /** Size of the avatar(s) */
  size?: AvatarSize
}

/**
 * MarketAvatar component - Tailwind replica of dapp-ui-v5 MarketAvatar
 *
 * Displays either a single loan avatar (for idle liquidity, which has no collateral)
 * or a loan/collateral pair. The loan asset leads, matching the loan-first market
 * identity (see `getMarketName`).
 *
 * @example
 * ```tsx
 * // Idle liquidity — no collateral
 * <MarketAvatar
 *   loanAssetURI="https://cdn.morpho.org/assets/logos/usdc.svg"
 *   size="medium"
 * />
 *
 * // Market pair — loan first
 * <MarketAvatar
 *   loanAssetURI="https://cdn.morpho.org/assets/logos/usdc.svg"
 *   collateralAssetURI="https://cdn.morpho.org/assets/logos/weth.svg"
 *   size="medium"
 * />
 * ```
 */
const MarketAvatar = forwardRef<HTMLDivElement, MarketAvatarProps>(
  ({ loanAssetURI, collateralAssetURI, size, className, ...props }, ref) => {
    // Only `undefined` means "no collateral asset" (idle liquidity). A `null` URI means
    // the collateral exists but has no logo, and still deserves its own fallback avatar —
    // testing truthiness here would make such a market look idle.
    if (collateralAssetURI === undefined) {
      return <Avatar ref={ref} src={loanAssetURI} size={size} className={className} {...props} />
    }

    // Show avatar pair for loan/collateral market
    return (
      <AvatarPair
        ref={ref}
        leftAvatarProps={{ src: loanAssetURI }}
        rightAvatarProps={{ src: collateralAssetURI }}
        size={size}
        className={className}
        {...props}
      />
    )
  }
)

MarketAvatar.displayName = 'MarketAvatar'

export { MarketAvatar }
